import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSearch, FiX, FiArrowRight } from 'react-icons/fi';
import { searchConversions } from '../../utils/searchUtils';
import { conversionData } from '../../utils/unitConversions/conversionData';
import { log } from '../../utils/logger';

const ConversionSearch = ({ onSelect, darkMode }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchRef = useRef(null);

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearching(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle search
  useEffect(() => {
    if (searchTerm.length >= 2) {
      log('Searching for term:', searchTerm); // Debug log
      const results = searchConversions(searchTerm, conversionData);
      log('Setting search results:', results); // Debug log
      setSearchResults(results);
      setIsSearching(true);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  }, [searchTerm]);

  const handleResultClick = (result) => {
    try {
      log('Handling result click:', result); // Debug log
      if (!result.category || !result.from || !result.to) {
        console.error('Invalid result format:', result);
        return;
      }
      
      if (onSelect) {
        onSelect({
          category: result.category,
          fromUnit: result.from,
          toUnit: result.to
        });
      } else {
        console.error('onSelect callback is not defined');
      }
      
      setSearchTerm('');
      setIsSearching(false);
    } catch (error) {
      console.error('Error handling search result:', error);
    }
  };

  return (
    <div className="relative group" ref={searchRef}>
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search conversions..."
        className={`w-full pl-9 pr-12 py-2 
          rounded-lg text-[12px]
          tracking-tight leading-relaxed
          transition-all duration-200
          outline-none shadow-sm
          border
          ${darkMode
            ? 'bg-gray-900/80 border-gray-700 text-neutral-100 placeholder:text-neutral-400'
            : 'bg-gray-50/60 border border-gray-200/70 text-gray-700 placeholder:text-gray-400 hover:border-gray-300/60'}
        `}
        aria-label="Search conversions"
        tabIndex={0}
      />
      <FiSearch className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${darkMode ? 'text-neutral-400' : 'text-gray-400/80'}`} />
      {searchTerm && (
        <button
          onClick={() => setSearchTerm('')}
          className={`absolute right-8 top-1/2 -translate-y-1/2 p-1 rounded-full transition-colors
            focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950
            ${darkMode ? 'hover:bg-gray-800 text-neutral-400 hover:text-primary-400' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'}`}
          aria-label="Clear search"
          tabIndex={0}
        >
          <FiX className="w-3.5 h-3.5" />
        </button>
      )}
      <kbd className={`absolute right-2.5 top-1/2 -translate-y-1/2 hidden lg:inline-flex items-center gap-0.5 px-1.5 h-4 text-[9px] font-medium rounded border
        ${darkMode ? 'bg-gray-800 text-neutral-400 border-gray-700' : 'bg-gray-100/80 text-gray-500/90 border-gray-200/60'}`}>⌘K</kbd>
      {/* Search Results Dropdown - Improved styling */}
      <AnimatePresence>
        {isSearching && searchResults.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className={`absolute w-full mt-1.5 rounded-lg shadow-lg overflow-hidden z-50 border
              ${darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200/80'}`}
            role="listbox"
          >
            {searchResults.map((result, index) => (
              <button
                key={index}
                className={`w-full px-3 py-2 text-left flex flex-col gap-0.5 border-b last:border-0 transition-colors
                  group
                  hover:bg-primary-50 dark:hover:bg-primary-900/20
                  ${darkMode ? 'text-neutral-100 border-gray-800/60' : 'text-gray-700 border-gray-100/60'}
                `}
                tabIndex={0}
                role="option"
                aria-selected={false}
                onClick={() => {
                  onSelect({
                    category: result.category,
                    fromUnit: result.from,
                    toUnit: result.to
                  });
                  setSearchTerm('');
                  setIsSearching(false);
                }}
              >
                <div className="flex items-center gap-2 text-[12px]">
                  <span className="font-medium">{result.from}</span>
                  <FiArrowRight className={`${darkMode ? 'text-neutral-400' : 'text-gray-400/80'} w-3 h-3 group-hover:text-primary-400 dark:group-hover:text-primary-300 transition-colors`} />
                  <span>{result.to}</span>
                </div>
                <span className={`text-[10px] ${darkMode ? 'text-neutral-400' : 'text-gray-500'}`}>{result.categoryName}</span>
              </button>
            ))}
          </motion.div>
        )}
        {isSearching && searchTerm.length >= 2 && searchResults.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className={`absolute w-full mt-1.5 rounded-lg shadow-lg p-3 z-50 border
              ${darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200/80'}`}
          >
            <div className="flex flex-col items-center gap-1.5">
              <span className={`text-[12px] ${darkMode ? 'text-neutral-300' : 'text-gray-600'}`}>No results found for "{searchTerm}"</span>
              <span className={`text-[11px] ${darkMode ? 'text-neutral-500' : 'text-gray-400'}`}>Try different keywords or browse categories</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ConversionSearch; 